import { useQaRefusalStore } from 'stores/qaRefusalStore';
import { isQaRefusalToken } from './qaRefusalOptions';

const QA_REFUSAL_STORAGE_KEY = 'qaRefusalTrigger';

const readCapturedToken = (): string | null => {
  try {
    return sessionStorage.getItem(QA_REFUSAL_STORAGE_KEY);
  } catch {
    return null;
  }
};

const clearCapturedToken = (): void => {
  try {
    sessionStorage.removeItem(QA_REFUSAL_STORAGE_KEY);
  } catch {
    // sessionStorage unavailable — nothing to clear.
  }
};

// Web navigation rewrites the URL to the current screen, dropping the original
// ?qaRefusal= before the card-add screen opens. Capture the token at app boot
// while it is still in the URL and persist it for the browser session.
export const captureQaRefusalTrigger = (search: string): void => {
  const fromQuery = new URLSearchParams(search).get('qaRefusal');
  if (!isQaRefusalToken(fromQuery)) {
    return;
  }
  try {
    sessionStorage.setItem(QA_REFUSAL_STORAGE_KEY, fromQuery);
  } catch {
    // sessionStorage unavailable — the injection just won't persist.
  }
};

// Web token source. Precedence: live ?qaRefusal= query param, then the
// boot-captured session token (consumed on use, so a captured token forces
// exactly one decline and never poisons a later same-tab checkout), then the
// persistent DevScreen selection (which renders on local web). The TEST-only
// environment gate in useAdyenCardAdd is what makes this inert in production.
export const getQaRefusalToken = (): string | null => {
  let fromQuery: string | null = null;
  try {
    const search = (globalThis as { location?: { search?: string } }).location
      ?.search;
    fromQuery = new URLSearchParams(search ?? '').get('qaRefusal');
  } catch {
    // Ignore a malformed location and fall through.
  }
  const oneShot = isQaRefusalToken(fromQuery) ? fromQuery : readCapturedToken();
  if (isQaRefusalToken(oneShot)) {
    clearCapturedToken();
    return oneShot;
  }
  const fromStore = useQaRefusalStore.getState().token;
  return isQaRefusalToken(fromStore) ? fromStore : null;
};
